import React, { useEffect } from "react";
import { Box, Typography, Container, Divider, Button, Icon, Link } from "@mui/material";
import { useTheme } from "@emotion/react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import Navbar from "./blocks/Navbar.js";
import Footer from "./blocks/Footer.js";
import { useContext } from "react";
import { AuthContext } from "src/utils/AuthContext.js";
import { getPageRoute } from "src/services";
import signatureImage from "../assets/signature.png";

const Vision = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Helmet>
        <title>Our Vision - Stanza</title>
        <meta name="description" content="Conscious capitalism meets community building. Discover how Stanza is creating a perpetual giving machine that brings people together around what they love." />
      </Helmet>
      <Navbar user={user} />
      <Box sx={{ margin: "auto", maxWidth: { xs: 'calc(100% - 2rem)', sm: "600px" }, minWidth: { xs: 'calc(100% - 2rem)', sm: "600px" }, marginTop: 0, flexGrow: 1, p: 2 }}>
        
        {/* Header */}
        <Typography as="h1" variant="h3" sx={{ mb: 8, mt: { md: 6 } }}>
            We do things differently.
        </Typography>

        {/* Main Content */}
        <Typography variant="title" sx={{ fontWeight: 100}}>
            The New World Order
        </Typography>
        <Typography as="p" variant="body1" sx={{ mt: 2, mb: 6 }}>
            <b>60,000</b> calendars, <b>10M+</b> subscribers – at any point there are <b>1B+</b> events in people’s calendars 
            we’re responsible for keeping updated. With each day bringing new creators and subscribers to the platform. 
            <br /><br />
            All of this is supported by just a handful of Stanzans. 
            <br /><br />
            We are one of many the AI-era is ushering in – what I’d like to call, lemonade stand companies. 
            Tiny teams with massive impact.  It has never been faster to go from idea to product to getting meaningful feedback from users. 
            <br /><br />
            And we’re leaning in. Part of building Stanza is tinkering at the cutting edge of what small teams can accomplish, at scale. 
            With a better AI model always around the corner, the ceiling continues to rise. 
        </Typography>

        {/* Tech Doesn't Have to Get Shitty */}
        <Typography variant="title" sx={{ fontWeight: 100}}>
            Tech Doesn't Have to Get Shitty
        </Typography>
        <Typography as="p" variant="body1" sx={{ mb: 6, mt: 2 }}>
            For better or worse, company building in tech has historically required subsequent rounds of funding coupled with an exit strategy. 
            But what if you can have a world without the tension between short-term shareholder profits and putting the user first? 
            <br /><br />
            A world where beloved products don’t have to bite the dust — either enshittifying to meet metrics — or sliding into oblivion post acquisition. 
            Or worse, selling your data with their final gasp. 
            <br /><br />
            That’s the world many builders are operating in now, including us. 
            <br /><br />
            Every product, line of code, blog post, how we price… 
            all the pieces that make Stanza what it is are thoughtfully designed from the lens of creators & subscribers first. 
            Not just first, <i>but only</i>. 
            <br /><br />
            And there’s immense relief in not having to make that tradeoff anymore. 
        </Typography>
         
        {/* Conscious Capitalism */}
         <Typography variant="title" sx={{ fontWeight: 100 }}>
           Conscious Capitalism
        </Typography>
        <Typography as="p" variant="body1" sx={{ mb: 6, mt: 2 }}>
            This expansive yet uncharted territory comes with the responsibility of asking the questions: 
            How do we give back? How do we contribute towards responsible AI where the financial 
            benefits aren’t seen by just a few?   
            <br /><br />
            We’re lucky to stand on the shoulders of giants – Patagonia is a company that I’ve been much inspired by. 
            And it never crossed my mind (until now) that we could follow in their footsteps. 
            <br /><br />
            Turns out 1% of profits from a company with a team that comprises a handful of people can quickly become quite significant.
        </Typography>

        {/* Human Connection */}
        <Typography variant="title" sx={{ fontWeight: 100 }}>
           Human Connection
        </Typography>
        <Typography as="p" variant="body1" sx={{ mb: 6, mt: 2 }}>
            We're also at the intersection of another watershed moment of our time — the loneliness epidemic with stats that just get more damning. If we have any hope of collectively thriving, 
            it is going to take all of us doing what we can with all the resources we have. 
            <br /><br />
            For so long Stanza has been synonymous with calendars — after all, that's what we do. 
            But behind every calendar is a community. A community that has common ground and something to look forward to, collectively. 
            When people come together around a shared experience, it is magical. 
            <br /><br />
            And as we’ve heard from our creators, worth dedicating your life's work to.
        </Typography>

        {/* The Creator Fund */}
        <Typography variant="title" sx={{ fontWeight: 100 }}>
           Stanza's Creator Fund
        </Typography>    
        <Typography as="p" variant="body1" sx={{ mb: 6, mt: 2 }}>
            That's our plan – we're going well beyond 1% of profits. Every dollar we don't reinvest in the business or pay our team ultimately ends up in our{" "}
            <Link 
              href="https://creatorhub.stanzacal.com/creator-fund/"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ color: theme.palette.primary.main, textDecoration: "underline", fontWeight: 600 }}
            >
              Creator Fund
            </Link>, deployed across communities that bring people together. 
            <br /><br />
            This also means that the majority of your Stanza+ membership or your Creator plan goes back to initiatives that make people feel like they belong. This will continue for as long as Stanza is in existence.
            <br /><br />
            This is our inaugural year of the fund and we're unbelievably excited to see the coming impact. 
            You'll be right there by our side, the first to know. 
        </Typography>
        {/* Call to Action */}
        <Typography variant="title" sx={{ fontWeight: 100 }}>
           Join Us
        </Typography>
        <Typography as="p" variant="body1" sx={{ mb: 6, mt: 2 }}>
            As a{" "}
            <Link 
              onClick={() => navigate(getPageRoute("upgrade", "UPGRADE_PLUS"))}
              sx={{ 
                color: theme.palette.primary.main, 
                textDecoration: "underline",
                background: "none",
                border: "none",
                padding: 0,
                font: "inherit",
                cursor: "pointer",
                fontWeight: 600
              }}
            >
              Stanza+ member
            </Link>{" "}
            or as a{" "}
            <Link 
              onClick={() => navigate("/import")}
              sx={{ 
                color: theme.palette.primary.main, 
                textDecoration: "underline",
                background: "none",
                border: "none",
                padding: 0,
                font: "inherit",
                cursor: "pointer",
                fontWeight: 600
              }}
            >
              creator
            </Link>{" "}
            on the platform, or perhaps both. 
            We're so happy you're here and part of our journey. We love building for you and are grateful beyond words to have this opportunity to build a new kind of company. 
            We wouldn't be here without you. 
            <br /><br />
            Our{" "}
            <Link 
              href="mailto:founders@stanza.co"
              sx={{ 
                color: theme.palette.primary.main, 
                textDecoration: "underline",
                fontWeight: 600
              }}
            >
              inboxes
            </Link>{" "}
            are open if you have feedback, thoughts or just well wishes.
            
        </Typography>

        {/* Signature Section */}
        <Box sx={{ textAlign: "left", mb: 12 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Cheers,
          </Typography>
          <Box component="img" 
            src={signatureImage} 
            alt="Smita's signature" 
            sx={{ 
              maxWidth: "120px",
              height: "auto",
              mb: 1,
              ml: -1.5
            }} 
          />
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            Smita Saxena
            <br />
            Founder
          </Typography>
        </Box>        
      </Box>
      <Footer />
    </div>
  );
};

export default Vision; 